import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { ActionButton } from "../../../components/Forms/ActionButton";
import RichTextEditor from "../../../components/Forms/RichTextEditor";
import ActiveStatus from "../../../components/Common/ActiveStatus";
import { useGetRewardsPageText, useUpdateRewardsPageText } from "../../../queries/rewardsConfig";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const RewardsPageTextController = ({
    screenName
}) => {

    const [active, setActive] = useState(true);
    const [show, setShow] = useState(true);

    const { data, isPending } = useGetRewardsPageText(screenName);
    const { mutateAsync, isPending: isUpdating } = useUpdateRewardsPageText();

    const renderHeading = (screenName) => {
        switch (screenName) {
            case "marketplace":
                return "Marketplace Screen Display Text";
            case "plant_a_tree":
                return "Plant a Tree Screen Display Text";
            case "green_projects":
                return "Green Projects Screen Display Text";
            default:
                return "Rewards Page Text";
        }
    }

    const renderScreenName = (screenName) => {
        switch (screenName) {
            case "marketplace":
                return "Marketplace";
            case "plant_a_tree":
                return "Plant a Tree";
            case "green_projects":
                return "Green Projects";
            default:
                return "Rewards Page";
        }
    }
    
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        watch,
    } = useForm({
        defaultValues: {
            screenName: '',
            heroText: '',
            howDoesItWorkText: '',
        }
    });

    const watchHowDoesItWorkText = watch("howDoesItWorkText");
    
    const onSubmit = async (data) => {
        try {
            await mutateAsync({
                screenName: screenName,
                heroText: data.heroText,
                howDoesItWorkText: data.howDoesItWorkText,
                active: active,
            }, {
                onSuccess: res => {
                    if (res.error) {
                        toast.error("Something went wrong while updating rewards page text");
                        return;
                    }
                    toast.success("Rewards page text updated successfully");
                },
                onError: (error) => {
                    toast.error("Failed to update rewards page text: " + (error.message || "Unknown error"));
                }
            });
        } catch (error) {
            console.error("Error updating rewards page text:", error);
            toast.error("Failed to update rewards page text");
        }
    }

    const handleActiveToggle = useCallback((newActiveState) => {
        setActive(newActiveState);
    }, []);

    useEffect(() => {
        if (data && typeof data === 'object') {
            setValue("screenName", data.screenName || '');
            setValue("heroText", data.heroText || '');
            setValue("howDoesItWorkText", data.howDoesItWorkText || '');
            setActive(data.active !== undefined ? data.active : true);
        }
    }, [data, setValue]);

    // Cleanup effect
    useEffect(() => {
        return () => {
            // Cleanup any pending operations
            if (typeof setActive === 'function') {
                setActive(true);
            }
        };
    }, []);

    if (isPending) {
        return <div>Loading...</div>;
    }

    return (
        <div className='bg-white shadow-md rounded-lg p-5 flex flex-col items-start gap-2'>
            <div className="flex items-center justify-between w-full">
                <h2 className="text-2xl text-primary font-bold cursor-pointer">
                    {renderHeading(screenName)}
                    <button className="text-black ml-2 text-base font-bold" onClick={() => setShow(!show)}>{show ? "Hide" : "Show"}</button>
                </h2>
                <ActiveStatus
                    active={active}
                    setActive={handleActiveToggle}
                    innerActiveText="Show"
                    innerInactiveText="Hide"
                    label={`Display on ${renderScreenName(screenName)} Screen`}
                />
            </div>
            {
                show && (
                    <form className="space-y-4 w-full" onSubmit={handleSubmit(onSubmit)}>

                        <div className='mt-2'>
                            <p className="text-base font-medium"><span className="text-red">*</span> Hero Text:</p>
                            <div className={`flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border ${errors.heroText ? 'border-red' : 'border-border'} text-slate-400`}>
                                <input
                                    type="text"
                                    name="heroText"
                                    id="heroText"
                                    {...register("heroText", { required: "Hero text is required", minLength: { value: 3, message: "Hero text must be at least 3 characters" } })}
                                    placeholder="Hero Text"
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-none px-4 py-2.5 rounded-md text-base`}
                                    min={0}
                                />
                            </div>
                            {
                                errors.heroText && (
                                    <p className="text-red text-sm mt-1">
                                        {errors.heroText.message || "This field is required"}
                                    </p>
                                )
                            }
                        </div>

                        <div className='mt-2'>
                            <RichTextEditor
                                label="How Does It Work Text"
                                value={watchHowDoesItWorkText}
                                placeholder="Enter how does it work text..."
                                onChange={(value) => setValue('howDoesItWorkText', value)}
                                required={true}
                            />
                            {
                                errors.howDoesItWorkText && (
                                    <p className="text-red text-sm mt-1">
                                        {errors.howDoesItWorkText.message || "This field is required"}
                                    </p>
                                )
                            }
                        </div>

                        <div className='mt-2 flex justify-end'>
                            <ActionButton
                                type={'submit'}
                                variant={'submit'}
                                text={'Update Configuration'}
                                disabled={isUpdating}
                            />
                        </div>
                    </form>
                )
            }
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
}

export default RewardsPageTextController;